import { Trans } from '@lingui/macro';
import { useEffect, useState, useMemo } from "react";
import { Search, X, Check, UserPlus } from "lucide-react";

import { Room } from "../../models/Room";
import { User, UserSimplified } from "../../models/User";
import { Fire } from "../../services/FireClient";
import { My } from "../../services/My";

import { Input } from "../../components/ui/input";
import { Button } from "../../components/ui/button";
import { Avatar, AvatarImage, AvatarFallback } from "../../components/ui/avatar";
import { Separator } from "../../components/ui/separator";
import { Badge } from "../../components/ui/badge";

interface UserFindProps {
  onSelectedUser: (user: User) => void,
  // Optional: batch add callback. If provided, it will be used when adding multiple users at once.
  onSelectedUsers?: (users: User[]) => void,
  placeholder: string,
  room: Room | undefined
}

const UserFindLocal: React.FC<UserFindProps> = ({ onSelectedUser, onSelectedUsers, placeholder, room }) => {
  const [searchValue, setSearchValue] = useState("");
  const [allUsers, setAllUsers] = useState<UserSimplified[]>([]);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const isCreator = room?.creatorId && room.creatorId == My.user?.id;

  useEffect(() => {
    // Only fetch users if we are in a room context
    const _users = My.getUsersFromAllRooms().filter(u => !room?.userIds.includes(u.id));
    setAllUsers(_users);
    setSelectedIds(new Set());
  }, [room?.id, JSON.stringify(room?.userIds)]);

  const filteredUsers = useMemo(() => {
    if (!searchValue.trim()) return allUsers;
    const lower = searchValue.toLowerCase();
    return allUsers.filter(u =>
      u.name.toLowerCase().includes(lower) ||
      (u.email && u.email.toLowerCase().includes(lower))
    );
  }, [allUsers, searchValue]);

  const toggleSelect = (userId: string) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(userId)) next.delete(userId);
      else next.add(userId);
      return next;
    });
  }

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && selectedIds.size > 0) {
      e.preventDefault();
      addSelected();
    }
  }

  const addSelected = async () => {
    if (selectedIds.size === 0) return;
    const ids = Array.from(selectedIds);
    const fullUsers = (await Promise.all(ids.map(id => Fire.getUser(id)))).filter((u): u is User => !!u);

    if (onSelectedUsers) {
      onSelectedUsers(fullUsers);
    } else {
      fullUsers.forEach(u => onSelectedUser(u));
    }

    // clear selection and search
    setSelectedIds(new Set());
    setSearchValue("");
  }

  if (!isCreator) return null;

  return (
    <div className="flex flex-col h-full w-full bg-white rounded-lg border shadow-sm overflow-hidden">
      {/* Search Header */}
      <div className="p-3 bg-gray-50/50 space-y-2">
        <div className="relative">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <Input
            value={searchValue}
            onChange={(e) => setSearchValue(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder={placeholder}
            className="pl-9 h-9 bg-white"
            autoFocus
          />
          {searchValue && (
            <button
              onClick={() => setSearchValue("")}
              className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
            >
              <X className="w-3 h-3" />
            </button>
          )}
        </div>
      </div>

      <Separator />

      {/* List Header */}
      <div className="flex items-center justify-between px-3 py-2 bg-white border-b">
        <span className="text-xs font-semibold text-gray-500 uppercase tracking-wider">
          <Trans>Select users</Trans> ({filteredUsers.length})
        </span>
        <Button
          size="sm"
          variant="default"
          onClick={addSelected}
          disabled={selectedIds.size === 0}
          className={`h-7 px-2 gap-1.5 ${selectedIds.size > 0 ? 'bg-orange-500 hover:bg-orange-600 text-white' : 'bg-gray-100 text-slate-600'}`}
        >
          <UserPlus className="w-3 h-3" />
          <Trans>Add selected</Trans>
          {selectedIds.size > 0 && (
            <Badge variant="secondary" className="ml-0.5 h-4 px-1 bg-white/20 text-white hover:bg-white/30 border-0 text-[10px]">
              {selectedIds.size}
            </Badge>
          )}
        </Button>
      </div>

      {/* User List */}
      <div className="flex-1 w-full overflow-y-auto">
        <div className="p-2 space-y-1">
          {filteredUsers.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-8 text-gray-400 text-sm">
              {searchValue ? <Trans>No user found</Trans> : <Trans>No members available to add</Trans>}
            </div>
          ) : (
            filteredUsers.map(u => {
              const isChecked = selectedIds.has(u.id);
              return (
                <div
                  key={u.id}
                  onClick={() => toggleSelect(u.id)}
                  className={`flex items-center gap-3 p-2 rounded-md cursor-pointer transition-colors border border-transparent ${isChecked ? 'bg-orange-50 border-orange-200' : 'hover:bg-gray-50'}`}
                >
                  <Avatar className="w-8 h-8 border border-gray-100">
                    <AvatarImage src={u.photoUrl ?? undefined} />
                    <AvatarFallback>{u.name.substring(0, 2)}</AvatarFallback>
                  </Avatar>

                  <div className="flex-1 min-w-0 grid gap-0.5">
                    <div className="text-sm font-medium leading-none truncate">{u.name}</div>
                    <div className="text-xs text-gray-500 truncate">{u.email || ''}</div>
                  </div>

                  <div className={`w-5 h-5 rounded-full border flex items-center justify-center transition-colors ${isChecked ? 'bg-orange-500 border-orange-500 text-white' : 'border-gray-300'}`}>
                    {isChecked && <Check className="w-3 h-3" />}
                  </div>
                </div>
              )
            })
          )}
        </div>
      </div>
    </div>
  );
}

export default UserFindLocal;